import { Controller } from "stimulus"
import { sendEmailQueue } from "../lib/email_send_queue"

export default class extends Controller {
  static targets = ["checkbox", "selectAll", "replaySubmit", "resendSubmit", "count", "progress", "currentRecipient", "progressSummary", "failures", "cancel"]

  connect() {
    this.sending = false
    this.cancelRequested = false
    this.beforeUnload = (event) => {
      if (!this.sending) return
      event.preventDefault()
      event.returnValue = ""
    }
    this.beforeVisit = (event) => {
      if (!this.sending) return
      event.preventDefault()
      window.alert("Email sending is in progress. Stop sending before leaving this page.")
    }
    document.addEventListener("turbolinks:before-visit", this.beforeVisit)
    this.update()
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.beforeUnload)
    document.removeEventListener("turbolinks:before-visit", this.beforeVisit)
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.checkboxTargets.forEach((checkbox) => { checkbox.checked = checked })
    this.update()
  }

  update() {
    const checked = this.checkedIds()
    const count = checked.length

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = count > 0 && count === this.checkboxTargets.length
      this.selectAllTarget.indeterminate = count > 0 && count < this.checkboxTargets.length
    }
    if (this.hasReplaySubmitTarget) this.replaySubmitTarget.disabled = count === 0
    if (this.hasResendSubmitTarget) this.resendSubmitTarget.disabled = count === 0
    if (this.hasCountTarget) this.countTarget.textContent = `${count} selected`
  }

  submitReplay() { this.submitForm("selected-replay-form") }

  submitResend() { this.sendQueue("selected-resend-form") }

  cancelQueue() {
    this.cancelRequested = true
    if (this.hasProgressSummaryTarget) this.progressSummaryTarget.textContent = "Stopping after the current email…"
  }

  checkedIds() {
    return this.checkboxTargets.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value)
  }

  async sendQueue(formId) {
    if (this.sending) return
    const form = document.getElementById(formId)
    const recipientIds = this.checkedIds()
    if (!form || recipientIds.length === 0) return
    if (form.dataset.missingLinkMessage) {
      window.alert(form.dataset.missingLinkMessage)
      return
    }
    const formEntries = Array.from(new FormData(form).entries())

    this.sending = true
    document.body.classList.add("email-queue-active")
    this.cancelRequested = false
    this.queueCounts = { sent: 0, skipped: 0, failed: 0, unknown: 0 }
    this.failuresTarget.innerHTML = ""
    this.setControlsDisabled(true)
    this.progressTarget.closest("[data-email-queue-status]").hidden = false
    this.renderQueueProgress(recipientIds.length)
    window.addEventListener("beforeunload", this.beforeUnload)

    await sendEmailQueue({
      form,
      recipientIds,
      formEntries,
      cancelled: () => this.cancelRequested,
      onStart: (current) => this.renderCurrentRecipient(current),
      onResult: (result) => {
        const status = Object.prototype.hasOwnProperty.call(this.queueCounts, result.status) ? result.status : "unknown"
        this.queueCounts[status] += 1
        if (status === "failed" || status === "unknown") this.renderQueueFailure(result, status)
        this.renderQueueProgress(recipientIds.length)
      }
    })

    this.sending = false
    document.body.classList.remove("email-queue-active")
    window.removeEventListener("beforeunload", this.beforeUnload)
    this.setControlsDisabled(false)
    const processed = Object.values(this.queueCounts).reduce((sum, count) => sum + count, 0)
    this.progressTarget.textContent = this.cancelRequested ? `Stopped after ${processed} of ${recipientIds.length}` : `Finished processing ${processed} emails`
    this.currentRecipientTarget.textContent = this.cancelRequested ? "Sending stopped." : "All selected emails have been processed."
    if (this.queueCounts.failed > 0 || this.queueCounts.unknown > 0) {
      this.progressSummaryTarget.textContent += ". Failed or uncertain recipients remain available after reloading."
    } else if (!this.cancelRequested) {
      window.setTimeout(() => window.location.reload(), 1000)
    }
  }

  renderQueueFailure(result, status) {
    const checkbox = this.checkboxTargets.find((target) => target.value === result.recipientId)
    const emailElement = checkbox && checkbox.closest(".events-admin-invitations__invited-user").querySelector("small")
    const email = (result.payload && result.payload.recipient_email) || (emailElement && emailElement.textContent.trim())
    const item = document.createElement("li")
    item.textContent = `${email || `Recipient ${result.recipientId}`}: ${status === "failed" ? "not sent" : "delivery uncertain"}`
    this.failuresTarget.append(item)
  }

  renderCurrentRecipient({ recipientId, index, total }) {
    const checkbox = this.checkboxTargets.find((target) => target.value === recipientId)
    const emailElement = checkbox && checkbox.closest(".events-admin-invitations__invited-user").querySelector("small")
    const email = emailElement && emailElement.textContent.trim()
    this.currentRecipientTarget.textContent = `Sending ${index} of ${total}${email ? ` to ${email}` : ""}…`
  }

  renderQueueProgress(total) {
    const processed = Object.values(this.queueCounts).reduce((sum, count) => sum + count, 0)
    this.progressTarget.textContent = `Emails are being sent — ${processed} of ${total} processed`
    this.progressSummaryTarget.textContent = `${this.queueCounts.sent} sent · ${this.queueCounts.skipped} skipped · ${this.queueCounts.failed} failed · ${this.queueCounts.unknown} uncertain`
  }

  setControlsDisabled(disabled) {
    this.checkboxTargets.forEach((checkbox) => { checkbox.disabled = disabled })
    if (this.hasSelectAllTarget) this.selectAllTarget.disabled = disabled
    if (this.hasReplaySubmitTarget) this.replaySubmitTarget.disabled = disabled
    if (this.hasResendSubmitTarget) this.resendSubmitTarget.disabled = disabled
    if (this.hasCancelTarget) this.cancelTarget.hidden = !disabled
  }

  submitForm(formId) {
    const form = document.getElementById(formId)
    if (!form) return

    form.querySelectorAll("[data-invitee-selection-injected]").forEach((input) => input.remove())

    this.checkedIds().forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "user_ids[]"
      input.value = id
      input.setAttribute("data-invitee-selection-injected", "true")
      form.appendChild(input)
    })

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }
}
